/**
 * PATH: packages/bills/src/components/BillAllocation.tsx
 * FILE_ID: BILLS-PKG-CMP-ALLOC-001
 * PURPOSE: Surface the evidence-based job allocation of a bill — a
 *          status pill for list rows + a full review section for the
 *          detail rail (status, linked job, confidence, source, and the
 *          per-step evidence bundle).
 *
 * WHY: The allocation pipeline (report/services/bill_allocation) only
 *      auto-links a bill to a job on a VERIFIED >=0.95 match (PO or
 *      exact job number); everything weaker is held for human review.
 *      A bookkeeper needs to (a) spot at a glance which bills need a
 *      decision, and (b) see WHY the AI linked / held a bill before
 *      confirming or overriding it. This component renders both, purely
 *      from the bill payload — no new client method required.
 *
 * FLOW:
 *   ┌──────────────┐     ┌──────────────────┐     ┌────────────────┐
 *   │ BillListRow  │ ──→ │ BillAllocation*  │ ──→ │ status pill /  │
 *   │ /BillDetail  │     │ (pure render)    │     │ review section │
 *   └──────────────┘     └──────────────────┘     └────────────────┘
 *
 * DEPENDENCIES: react + ../types only (zero runtime deps — this is a
 *               shared package).
 */

import type {
  BillAllocationFinding,
  BillAllocationSource,
  BillAllocationStatus,
  BillDetail,
  BillListRow,
} from '../types';
import { Section } from './BillFieldPrimitives';

const STATUS_META: Record<
  BillAllocationStatus,
  { label: string; cls: string; hint: string }
> = {
  linked: {
    label: 'Linked',
    cls: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200',
    hint: 'Auto-linked on a verified match to exactly one job.',
  },
  suggested: {
    label: 'Suggested',
    cls: 'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200',
    hint: 'A candidate job was found but is not verified — confirm it.',
  },
  needs_review: {
    // "Pick job" over the vague "Needs review" in the LIST chip — it names
    // the action the bookkeeper must take. The detail section keeps the
    // longer hint below.
    label: 'Pick job',
    cls: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
    hint: 'No verified job match — a human must pick the job.',
  },
  unallocated: {
    label: 'Unallocated',
    cls: 'bg-muted text-muted-foreground',
    hint: 'The allocator has not linked this bill to a job yet.',
  },
};

/** Tiny inline link glyph for the list chip — an SVG literal keeps this
 * shared module at its documented zero-runtime-deps contract. */
function LinkGlyph() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-2.5 w-2.5 shrink-0"
    >
      <path d="M9 17H7A5 5 0 0 1 7 7h2" />
      <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
      <line x1="8" x2="16" y1="12" y2="12" />
    </svg>
  );
}

const SOURCE_LABEL: Record<BillAllocationSource, string> = {
  '': '',
  po: 'PO match',
  job_number: 'Exact job #',
  fuzzy: 'Fuzzy match',
  ai: 'AI tie-break',
  manual: 'Manual',
  sender_history: 'Sender history',
};

/** Normalise the status off a (possibly legacy) row to a known key so
 * the render never crashes on an unexpected backend value. */
function statusOf(row: { allocation_status?: string | null }): BillAllocationStatus {
  const s = row.allocation_status;
  if (s === 'linked' || s === 'suggested' || s === 'needs_review') return s;
  return 'unallocated';
}

/** Format a 0–1 confidence (string or number) as a whole-percent label,
 * or null when absent/unparseable so callers can hide it. */
export function formatAllocationConfidence(
  raw: string | number | null | undefined,
): string | null {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : parseFloat(raw);
  if (!Number.isFinite(n)) return null;
  return `${Math.round(n * 100)}%`;
}

/**
 * Compact status chip for the bills list. Renders nothing for the
 * ``unallocated`` default so the list stays quiet until the allocator
 * has something to say (keeps the row scannable).
 *
 * Linked/suggested chips lead with a link glyph + the JOB NAME (the
 * fact the bookkeeper actually wants), with the status carried by the
 * chip colour + tooltip; the needs-review chip reads "Pick job" — the
 * action to take.
 */
export function BillAllocationBadge({ row }: { row: BillListRow }) {
  const status = statusOf(row);
  if (status === 'unallocated') return null;
  const meta = STATUS_META[status];
  const job = row.linked_job_name;
  const showJob = (status === 'linked' || status === 'suggested') && job;
  return (
    <span
      className={`inline-flex max-w-[11rem] items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${meta.cls}`}
      title={job ? `${meta.hint} (${job})` : meta.hint}
    >
      {showJob ? (
        <>
          <LinkGlyph />
          <span className="truncate">{job}</span>
        </>
      ) : (
        meta.label
      )}
    </span>
  );
}

/**
 * Full allocation review section for the detail rail. Shows the status,
 * the linked job (or the held candidate), confidence + source, and the
 * step-by-step evidence bundle so the bookkeeper can audit the decision.
 *
 * Read-only by design: linking/confirming a job is handled by the host
 * app's job-attribution flow (POST /api/bills/<id>/link-job/, which is
 * now hardened to validate the report.Job exists). This section makes
 * the AI's reasoning transparent; it does not mutate.
 */
export function BillAllocationSection({ bill }: { bill: BillDetail }) {
  const status = statusOf(bill);
  const meta = STATUS_META[status];
  const conf = formatAllocationConfidence(bill.allocation_confidence);
  const source = bill.allocation_source
    ? SOURCE_LABEL[bill.allocation_source] || bill.allocation_source
    : null;
  const findings = bill.allocation_evidence?.findings ?? [];

  return (
    <Section title="Job allocation">
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}
        >
          {meta.label}
        </span>
        {conf && (
          <span className="rounded-full border border-border bg-background px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
            {conf} confidence
          </span>
        )}
        {source && (
          <span className="rounded-full border border-border bg-background px-1.5 py-0.5 text-xs text-muted-foreground">
            {source}
          </span>
        )}
      </div>

      {bill.linked_job_id ? (
        <p className="text-xs text-foreground">
          Linked to{' '}
          <a
            href={`/jobs/${bill.linked_job_id}`}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary hover:underline"
          >
            {bill.linked_job_name || `Job #${bill.linked_job_id}`}
          </a>
          {bill.linked_po_id ? (
            <span className="text-muted-foreground"> · via PO #{bill.linked_po_id}</span>
          ) : null}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">{meta.hint}</p>
      )}

      {findings.length > 0 && <EvidenceList findings={findings} />}
    </Section>
  );
}

/** Render the ordered evidence bundle — one row per micro-step finding,
 * with a tick/cross for whether it matched and any detail/confidence. */
function EvidenceList({ findings }: { findings: BillAllocationFinding[] }) {
  return (
    <details className="rounded-md border border-border bg-background">
      <summary className="cursor-pointer select-none px-2 py-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground">
        Why? · {findings.length} evidence step{findings.length > 1 ? 's' : ''}
      </summary>
      <ul className="space-y-1 px-2 pb-2 pt-1">
        {findings.map((f, idx) => (
          <li key={idx} className="flex items-start gap-1.5 text-[11px]">
            <span aria-hidden className="mt-0.5">
              {f.matched ? '✓' : '·'}
            </span>
            <span className="min-w-0">
              <span className="font-medium">{f.step || f.source || 'step'}</span>
              {describeFinding(f) && (
                <span className="text-muted-foreground"> — {describeFinding(f)}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

/** Build a one-line human description of a finding from whichever keys
 * the backend populated. Kept defensive — the bundle evolves. */
function describeFinding(f: BillAllocationFinding): string {
  const bits: string[] = [];
  if (f.detail) bits.push(String(f.detail));
  if (f.po_number) bits.push(`PO ${f.po_number}`);
  if (f.job_number) bits.push(`job ${f.job_number}`);
  const conf = formatAllocationConfidence(f.confidence as string | number | null);
  if (conf) bits.push(conf);
  return bits.join(' · ');
}
